import { useRef, useState, useCallback, useEffect } from 'react';
import type {
  WhiteboardElement,
  ElementType,
  DeviceFrame,
  DeviceType,
  FlowArrow,
  SnapGuide,
} from '../../types/whiteboard';
import { createWhiteboardElement, DEVICE_FRAME_PRESETS } from '../../types/whiteboard';
import { WhiteboardElementRenderer } from './WhiteboardElementRenderer';
import { WhiteboardFrameRenderer } from './WhiteboardFrameRenderer';

interface Props {
  elements: WhiteboardElement[];
  frames: DeviceFrame[];
  arrows: FlowArrow[];
  selectedIds: string[];
  selectedFrameId: string | null;
  focusedFrameId: string | null;
  zoom: number;
  pan: { x: number; y: number };
  showGrid: boolean;
  gridSize: number;
  snapToGrid: boolean;
  activeTool: string | null;
  guides: SnapGuide[];
  onSelect: (ids: string[]) => void;
  onSelectFrame: (id: string | null) => void;
  onUpdate: (id: string, updates: Partial<WhiteboardElement>) => void;
  onDelete: (id: string) => void;
  onAddElement: (element: WhiteboardElement) => void;
  onPanChange: (pan: { x: number; y: number }) => void;
  // Frame ops
  onAddFrame: (deviceType: DeviceType, x: number, y: number) => void;
  onUpdateFrame: (id: string, updates: Partial<DeviceFrame>) => void;
  onDeleteFrame: (id: string) => void;
  onDuplicateFrame: (frame: DeviceFrame) => void;
  onChangeDevice: (frameId: string, deviceType: DeviceType) => void;
  // Arrow ops
  onAddArrow: (sourceId: string, sourceAnchor: string, targetId: string, targetAnchor: string) => void;
  onSelectArrow: (id: string | null) => void;
  selectedArrowId: string | null;
  // Frame child management
  findFrameAtPoint: (x: number, y: number) => DeviceFrame | null;
  onElementDuplicate: (element: WhiteboardElement) => void;
}

export function WhiteboardCanvas({
  elements,
  frames,
  arrows: _arrows,
  selectedIds,
  selectedFrameId,
  focusedFrameId,
  zoom,
  pan,
  showGrid,
  gridSize,
  snapToGrid,
  activeTool,
  guides,
  onSelect,
  onSelectFrame,
  onUpdate,
  onDelete,
  onAddElement,
  onPanChange,
  onAddFrame,
  onUpdateFrame,
  onDeleteFrame,
  onDuplicateFrame,
  onChangeDevice,
  onAddArrow: _onAddArrow,
  onSelectArrow,
  selectedArrowId: _selectedArrowId,
  findFrameAtPoint,
  onElementDuplicate,
}: Props) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0, panX: 0, panY: 0 });
  const [selectionBox, setSelectionBox] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [selectionStart, setSelectionStart] = useState<{ x: number; y: number } | null>(null);
  const [dropTargetFrameId, setDropTargetFrameId] = useState<string | null>(null);

  // Arrow drawing state
  const [arrowDrawing, setArrowDrawing] = useState<{
    sourceId: string;
    sourceAnchor: string;
    currentX: number;
    currentY: number;
  } | null>(null);

  // Grid pattern
  const gridStyle = showGrid ? {
    backgroundImage: `
      linear-gradient(to right, rgba(0, 0, 0, 0.04) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(0, 0, 0, 0.04) 1px, transparent 1px)
    `,
    backgroundSize: `${gridSize * zoom}px ${gridSize * zoom}px`,
    backgroundPosition: `${pan.x % (gridSize * zoom)}px ${pan.y % (gridSize * zoom)}px`,
  } : {};

  // Get canvas-relative coordinates
  const getCanvasCoords = useCallback((clientX: number, clientY: number) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (clientX - rect.left - pan.x) / zoom,
      y: (clientY - rect.top - pan.y) / zoom,
    };
  }, [pan, zoom]);

  // Snap to grid
  const snapCoord = useCallback((value: number) => {
    if (!snapToGrid) return value;
    return Math.round(value / gridSize) * gridSize;
  }, [snapToGrid, gridSize]);

  // Handle click on canvas to add element or frame
  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;

    const target = e.target as HTMLElement;
    if (target.closest('[data-element-id]') || target.closest('[data-frame-id]')) return;

    const coords = getCanvasCoords(e.clientX, e.clientY);
    const snappedX = snapCoord(coords.x);
    const snappedY = snapCoord(coords.y);

    // Frame placement tool
    if (activeTool && activeTool.startsWith('frame:')) {
      const deviceType = activeTool.replace('frame:', '') as DeviceType;
      onAddFrame(deviceType, snappedX, snappedY);
      return;
    }

    // Element placement tool
    if (activeTool && activeTool !== 'select' && activeTool !== 'pan' && activeTool !== 'arrow') {
      // Check if click is inside a frame
      const frame = findFrameAtPoint(coords.x, coords.y);

      const newElement = createWhiteboardElement(activeTool as ElementType, snappedX, snappedY);

      if (frame) {
        const preset = frame.deviceType !== 'custom' ? DEVICE_FRAME_PRESETS[frame.deviceType] : null;
        const chromeTop = 36 + ((preset?.chromeType === 'phone') ? 44 : 0);
        const relX = coords.x - frame.x;
        const relY = coords.y - frame.y - chromeTop + frame.scrollOffset;

        newElement.parentFrameId = frame.id;
        newElement.relativeX = Math.max(0, Math.min(frame.viewportWidth - newElement.width, snapCoord(relX)));
        newElement.relativeY = Math.max(0, snapCoord(relY));
        newElement.x = newElement.relativeX;
        newElement.y = newElement.relativeY;
      }

      onAddElement(newElement);
      onSelect([newElement.id]);
      return;
    }

    // Select tool: deselect
    if (activeTool === 'select') {
      onSelect([]);
      onSelectFrame(null);
      onSelectArrow(null);
    }
  }, [activeTool, getCanvasCoords, snapCoord, onAddElement, onSelect, onSelectFrame, onSelectArrow, onAddFrame, findFrameAtPoint]);

  // Mouse down for panning and selection box
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-element-id]') || target.closest('[data-frame-id]')) return;

    // Middle mouse or pan tool
    if (e.button === 1 || activeTool === 'pan') {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y });
      return;
    }

    // Selection box
    if (e.button === 0 && activeTool === 'select') {
      const coords = getCanvasCoords(e.clientX, e.clientY);
      setSelectionStart(coords);
      setSelectionBox({ x: coords.x, y: coords.y, w: 0, h: 0 });
    }
  }, [activeTool, pan, getCanvasCoords]);

  // Mouse move
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isPanning) {
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      onPanChange({ x: panStart.panX + dx, y: panStart.panY + dy });
      return;
    }

    if (selectionStart && selectionBox) {
      const coords = getCanvasCoords(e.clientX, e.clientY);
      const x = Math.min(selectionStart.x, coords.x);
      const y = Math.min(selectionStart.y, coords.y);
      const w = Math.abs(coords.x - selectionStart.x);
      const h = Math.abs(coords.y - selectionStart.y);
      setSelectionBox({ x, y, w, h });
    }

    // Arrow drawing
    if (arrowDrawing) {
      const coords = getCanvasCoords(e.clientX, e.clientY);
      setArrowDrawing(prev => prev ? { ...prev, currentX: coords.x, currentY: coords.y } : null);
    }
  }, [isPanning, panStart, selectionStart, selectionBox, arrowDrawing, getCanvasCoords, onPanChange]);

  // Mouse up
  const handleMouseUp = useCallback(() => {
    if (isPanning) {
      setIsPanning(false);
      return;
    }

    if (selectionBox && selectionBox.w > 5 && selectionBox.h > 5) {
      // Select free elements within box
      const freeElements = elements.filter(el => !el.parentFrameId);
      const selected = freeElements.filter(el => {
        const elRight = el.x + el.width;
        const elBottom = el.y + el.height;
        const boxRight = selectionBox.x + selectionBox.w;
        const boxBottom = selectionBox.y + selectionBox.h;
        return !(el.x > boxRight || elRight < selectionBox.x ||
                 el.y > boxBottom || elBottom < selectionBox.y);
      });
      onSelect(selected.map(el => el.id));
    }

    setSelectionBox(null);
    setSelectionStart(null);
    setDropTargetFrameId(null);
  }, [isPanning, selectionBox, elements, onSelect]);

  // Wheel for panning
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      onPanChange({
        x: pan.x - e.deltaX,
        y: pan.y - e.deltaY,
      });
    };

    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, [pan, onPanChange]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT' ||
          (e.target as HTMLElement).tagName === 'TEXTAREA') {
        return;
      }

      // Delete selected elements
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.length > 0) {
        e.preventDefault();
        selectedIds.forEach(id => onDelete(id));
        onSelect([]);
      }

      // Escape to deselect
      if (e.key === 'Escape') {
        onSelect([]);
        onSelectFrame(null);
        onSelectArrow(null);
      }

      // Select all free elements
      if (e.key === 'a' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        const freeElements = elements.filter(el => !el.parentFrameId);
        onSelect(freeElements.map(el => el.id));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIds, elements, onDelete, onSelect, onSelectFrame, onSelectArrow]);

  // Handle element selection
  const handleElementSelect = useCallback((id: string) => {
    onSelect([id]);
    onSelectFrame(null);
    onSelectArrow(null);
  }, [onSelect, onSelectFrame, onSelectArrow]);

  // Handle element update with grid snapping
  const handleElementUpdate = useCallback((id: string, updates: Partial<WhiteboardElement>) => {
    if (snapToGrid && (updates.x !== undefined || updates.y !== undefined)) {
      updates = {
        ...updates,
        x: updates.x !== undefined ? snapCoord(updates.x) : undefined,
        y: updates.y !== undefined ? snapCoord(updates.y) : undefined,
      };
    }
    onUpdate(id, updates);
  }, [snapToGrid, snapCoord, onUpdate]);

  // Handle frame selection
  const handleFrameSelect = useCallback((id: string) => {
    onSelectFrame(id);
    onSelect([]);
    onSelectArrow(null);
  }, [onSelectFrame, onSelect, onSelectArrow]);

  // Duplicate element
  const handleDuplicate = useCallback((element: WhiteboardElement) => {
    onElementDuplicate(element);
  }, [onElementDuplicate]);

  // Get cursor style
  const getCursor = () => {
    if (isPanning) return 'grabbing';
    if (activeTool === 'pan') return 'grab';
    if (activeTool === 'arrow') return 'crosshair';
    if (activeTool && activeTool !== 'select') return 'crosshair';
    return 'default';
  };

  // Separate free elements from frame children
  const freeElements = elements.filter(el => !el.parentFrameId);

  return (
    <div
      ref={canvasRef}
      className="w-full h-full bg-white overflow-hidden relative"
      style={{
        ...gridStyle,
        cursor: getCursor(),
      }}
      onClick={handleCanvasClick}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Canvas content with pan/zoom transform */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: '0 0',
        }}
      >
        {/* Render frames */}
        {frames.map(frame => {
          const childElements = elements.filter(el => el.parentFrameId === frame.id);
          return (
            <div key={frame.id} className="pointer-events-auto">
              <WhiteboardFrameRenderer
                frame={frame}
                isSelected={selectedFrameId === frame.id}
                isFocused={focusedFrameId === frame.id}
                childElements={childElements}
                selectedElementIds={selectedIds}
                zoom={zoom}
                activeTool={activeTool}
                highlightDrop={dropTargetFrameId === frame.id}
                onSelect={() => handleFrameSelect(frame.id)}
                onUpdate={(updates) => onUpdateFrame(frame.id, updates)}
                onDelete={() => onDeleteFrame(frame.id)}
                onDuplicate={() => onDuplicateFrame(frame)}
                onChangeDevice={(dt) => onChangeDevice(frame.id, dt)}
                onElementSelect={handleElementSelect}
                onElementUpdate={handleElementUpdate}
                onElementDelete={onDelete}
                onElementDuplicate={handleDuplicate}
              />
            </div>
          );
        })}

        {/* Render free elements (not inside frames) */}
        {freeElements.map(element => (
          <div key={element.id} className="pointer-events-auto">
            <WhiteboardElementRenderer
              element={element}
              isSelected={selectedIds.includes(element.id)}
              zoom={zoom}
              onSelect={() => handleElementSelect(element.id)}
              onUpdate={(updates) => handleElementUpdate(element.id, updates)}
              onDelete={() => onDelete(element.id)}
              onDuplicate={() => handleDuplicate(element)}
            />
          </div>
        ))}

        {/* Snap guides */}
        {guides.map((guide, i) => (
          <div
            key={i}
            className="absolute pointer-events-none"
            style={guide.type === 'vertical' ? {
              left: guide.position,
              top: guide.start,
              width: 1,
              height: guide.end - guide.start,
              borderLeft: '1px dashed var(--accent-600)',
              opacity: 0.6,
            } : {
              left: guide.start,
              top: guide.position,
              width: guide.end - guide.start,
              height: 1,
              borderTop: '1px dashed var(--accent-600)',
              opacity: 0.6,
            }}
          />
        ))}

        {/* Arrow drawing preview */}
        {arrowDrawing && (
          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ overflow: 'visible' }}>
            <line
              x1={arrowDrawing.currentX - 50}
              y1={arrowDrawing.currentY - 50}
              x2={arrowDrawing.currentX}
              y2={arrowDrawing.currentY}
              stroke="var(--accent-600)"
              strokeWidth={2}
              strokeDasharray="6 4"
            />
          </svg>
        )}
      </div>

      {/* Selection box */}
      {selectionBox && selectionBox.w > 2 && selectionBox.h > 2 && (
        <div
          className="absolute border-2 border-primary bg-primary/10 pointer-events-none"
          style={{
            left: selectionBox.x * zoom + pan.x,
            top: selectionBox.y * zoom + pan.y,
            width: selectionBox.w * zoom,
            height: selectionBox.h * zoom,
          }}
        />
      )}

      {/* Origin marker */}
      <div
        className="absolute w-4 h-4 pointer-events-none"
        style={{
          left: pan.x - 8,
          top: pan.y - 8,
        }}
      >
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-danger/30" />
        <div className="absolute top-1/2 left-0 right-0 h-px bg-danger/30" />
      </div>

      {/* Empty state */}
      {elements.length === 0 && frames.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center">
              <svg className="w-8 h-8 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18" />
                <path d="M9 21V9" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-default mb-2">Start designing</h3>
            <p className="text-sm text-muted max-w-xs">
              Add a device frame or click a component tool, then click on the canvas
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
